/**
 * Promotion Slot 시스템 API.
 *
 * 영구지침: docs/develop/promotion/promotion-slot-spec.md §6.1
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import { API_BASE_URL } from '@/lib/constants'
import { SLOT_ADFIT_UNITS } from '@/features/promotion/adfitSlots'

// =========================================================================
// 이미지 업로드 / URL 변환
// =========================================================================

/**
 * 어드민 등록 모달에서 사용하는 이미지 업로드 헬퍼.
 * 기존 /api/v1/files/upload 엔드포인트를 재사용한다 (시설 사진과 동일 패턴).
 *
 * @returns 저장된 MinIO 파일명 (확장자 포함)
 */
export async function uploadPromotionImage(file: File): Promise<string> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await apiClient.post('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  const filename = res.data?.data?.filename
  if (!filename) {
    throw new Error('업로드 응답에서 filename을 찾을 수 없습니다')
  }
  return filename
}

/**
 * 저장된 imageUrl 값을 화면에 표시할 수 있는 URL로 변환.
 * - http(s)로 시작하면 외부 URL → 그대로 사용
 * - 그 외에는 MinIO 파일명으로 보고 /api/v1/files/{filename}으로 변환
 */
export function toPromotionImageUrl(value: string | null | undefined): string | null {
  if (!value) return null
  if (value.startsWith('http://') || value.startsWith('https://')) return value
  return `${API_BASE_URL}/files/${value}`
}

// =========================================================================
// 타입
// =========================================================================

export type PromotionLinkTarget = 'SELF' | 'BLANK'
export type PromotionBadgeType =
  | 'AD'
  | 'SPONSORED'
  | 'EVENT'
  | 'NOTICE'
  | 'PREMIUM'
export type PromotionTargetType = 'ALL' | 'GUEST' | 'MEMBER' | 'REGION'
export type PromotionStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'EXPIRED'

/** 공개 송출용 응답 (PromotionPublicResponse) */
export interface PromotionPublic {
  id: number
  slotKey: string
  title: string
  description: string | null
  imageUrl: string | null
  imageUrlMobile: string | null
  linkUrl: string | null
  linkTarget: PromotionLinkTarget
  badgeType: PromotionBadgeType | null
}

/** 어드민용 응답 (PromotionResponse) */
export interface Promotion extends PromotionPublic {
  targetType: PromotionTargetType
  targetValue: string | null
  startsAt: string
  endsAt: string
  priority: number
  weight: number
  status: PromotionStatus
  impressions: number
  clicks: number
  ctr: number
  advertiserId: number | null
  facilityId: number | null
  createdBy: number
  createdAt: string
  updatedAt: string
}

/** 통계 조회 단위 */
export type StatsGranularity = 'DAY' | 'WEEK' | 'MONTH'

export interface PromotionStatsPoint {
  period: string
  label: string
  impressions: number
  clicks: number
  /** 클릭률(%) */
  ctr: number
}

export interface PromotionStats {
  slotId: number
  title: string
  granularity: StatsGranularity
  from: string
  to: string
  points: PromotionStatsPoint[]
  summary: {
    impressions: number
    clicks: number
    ctr: number
    /** 일별 집계 도입(V92) 이전의 동결 누적 — 일별로 쪼갤 수 없는 과거치 */
    legacyImpressions: number
    legacyClicks: number
    totalImpressions: number
    totalClicks: number
  }
}

/** 등록/수정 요청 */
export interface PromotionRequest {
  slotKey: string
  title: string
  description?: string
  imageUrl?: string
  imageUrlMobile?: string
  linkUrl?: string
  linkTarget?: PromotionLinkTarget
  badgeType?: PromotionBadgeType | null
  targetType?: PromotionTargetType
  targetValue?: string | null
  startsAt: string
  endsAt: string
  priority?: number
  weight?: number
  status?: PromotionStatus
  advertiserId?: number | null
  facilityId?: number | null
}

// =========================================================================
// 공개 송출
// =========================================================================

/**
 * 슬롯 송출 조회 훅.
 *
 * SSR-safe: 서버에서 prefetch한 데이터가 있으면 즉시 사용.
 * 클라이언트에서는 staleTime 동안 캐시된 데이터를 재사용.
 */
export function usePromotionSlot(
  slotKey: string,
  region?: string,
  limit: number = 1
) {
  return useQuery({
    queryKey: ['promotion-slot', slotKey, region, limit],
    queryFn: async () => {
      const params: Record<string, string> = { slotKey, limit: String(limit) }
      if (region) params.region = region
      const res = await apiClient.get('/promotions', { params })
      return (res.data.data ?? []) as PromotionPublic[]
    },
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * 노출 카운트 트래킹 (브라우저 전용).
 */
export async function trackImpression(id: number): Promise<void> {
  if (typeof window === 'undefined') return
  try {
    await apiClient.post(`/promotions/${id}/impression`)
  } catch {
    // 트래킹 실패는 무시 (사용자 경험 보호)
  }
}

/**
 * 클릭 트래킹 URL.
 * <a href={getClickUrl(id)}>로 사용하면 백엔드가 카운트 후 link_url로 redirect.
 */
export function getClickUrl(id: number): string {
  return `/api/v1/promotions/${id}/click`
}

// =========================================================================
// 어드민
// =========================================================================

interface SpringPage<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

export function useAdminPromotions(filter: {
  slotKey?: string
  status?: PromotionStatus | 'all'
  page?: number
  size?: number
}) {
  return useQuery({
    queryKey: ['admin-promotions', filter],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(filter.page ?? 0),
        size: String(filter.size ?? 20),
      }
      if (filter.slotKey) params.slotKey = filter.slotKey
      if (filter.status && filter.status !== 'all') params.status = filter.status
      const res = await apiClient.get('/admin/promotions', { params })
      return res.data.data as SpringPage<Promotion>
    },
  })
}

/**
 * 슬롯 하나의 노출·클릭 추이.
 *
 * days 는 오늘부터 거슬러 올라갈 일수. 주별·월별은 같은 데이터를 서버가 접어서 준다.
 */
export function useAdminPromotionStats(
  id: number | null,
  days: number,
  granularity: StatsGranularity,
) {
  return useQuery({
    queryKey: ['admin-promotion-stats', id, days, granularity],
    enabled: id != null,
    queryFn: async () => {
      const to = new Date()
      const from = new Date()
      from.setDate(from.getDate() - (days - 1))
      const iso = (d: Date) => d.toISOString().slice(0, 10)
      const res = await apiClient.get(`/admin/promotions/${id}/stats`, {
        params: { from: iso(from), to: iso(to), granularity },
      })
      return res.data.data as PromotionStats
    },
  })
}

/**
 * 슬롯 키 fallback.
 *
 * 영구지침 §3 (promotion-slot-spec.md)에 정의된 8개 슬롯 키와 동일.
 * 백엔드 API가 일시적으로 실패하거나 빈 배열을 반환해도 어드민 등록이
 * 막히지 않도록 클라이언트에서 동일 목록을 보유한다.
 *
 * 이 목록은 '하우스 전용' 슬롯의 fallback 이다. 애드핏이 매핑된 슬롯
 * (adfitSlots.ts SLOT_ADFIT_UNITS)은 useAdminSlotKeys 에서 자동 병합되므로
 * 여기에 다시 넣지 않아도 어드민 등록 대상에 포함된다.
 *
 * 슬롯 키를 변경할 때는 다음을 함께 업데이트해야 한다:
 *   1. promotion-slot-spec.md §3
 *   2. PromotionServiceImpl#AVAILABLE_SLOT_KEYS
 *   3. 본 FALLBACK_SLOT_KEYS (애드핏 슬롯이면 adfitSlots.ts 만으로 충분)
 *   4. entry-server.tsx prefetch 분기
 */
export const FALLBACK_SLOT_KEYS: readonly string[] = [
  'main_hero',
  'main_card_slider',
  'main_my_below',
  'main_side_ad_grid',
  'main_mobile_inline',
  'main_shop_card',
  'search_list_4',
  'search_list_12',
  'facility_detail_side',
  'simulator_result_side',
  'guide_bottom',
]

/**
 * 슬롯별 권장 배너 사이즈 (영구지침 promotion-slot-spec.md §3).
 *
 * - pc: PC 이미지 필드 권장 사이즈
 * - mobile: 별도 모바일 이미지를 쓰는 경우 (없으면 PC 이미지 재사용)
 *   main_hero 는 별도 모바일 버전 권장. facility_detail_side 는 PC 사이드바와
 *   모바일 본문 중간에 동일 슬롯으로 노출되므로 모바일 최적 이미지를 함께 올리면 좋다.
 */
export const SLOT_SIZE_HINTS: Record<string, { pc: string; mobile?: string }> = {
  main_hero: { pc: '1200×300', mobile: '720×360' },
  main_card_slider: { pc: '940×140 (가로 배너)', mobile: '720×200' },
  main_my_below: { pc: '360×140 (가로 배너)', mobile: '720×280' },
  main_side_ad_grid: { pc: '600×600 (정사각)', mobile: '600×600' },
  main_mobile_inline: { pc: '720×240', mobile: '720×240' },
  main_shop_card: { pc: '600×600 (정사각)', mobile: '600×600' },
  search_list_4: { pc: '400×140' },
  search_list_12: { pc: '400×140' },
  facility_detail_side: { pc: '250×250 (정사각)', mobile: '720×240' },
  simulator_result_side: { pc: '300×250' },
  guide_bottom: { pc: '600×200' },

  // 좌우 세로배너 160×600 (PC 초광폭에서만 노출)
  home_side_left: { pc: '160×600 (세로 배너)' },
  home_side_right: { pc: '160×600 (세로 배너)' },
  facility_detail_sticky_left: { pc: '160×600 (세로 배너)' },
  facility_detail_sticky_right: { pc: '160×600 (세로 배너)' },
  facility_search_side_right: { pc: '160×600 (세로 배너)' },
  checklist_side_left: { pc: '160×600 (세로 배너)' },
  checklist_side_right: { pc: '160×600 (세로 배너)' },
  simulator_side_left: { pc: '160×600 (세로 배너)' },
  simulator_side_right: { pc: '160×600 (세로 배너)' },
  community_post_side_left: { pc: '160×600 (세로 배너)' },
  community_post_side_right: { pc: '160×600 (세로 배너)' },
  community_board_side_left: { pc: '160×600 (세로 배너)' },
  community_board_side_right: { pc: '160×600 (세로 배너)' },

  // 모바일 전용 가로 배너 320×100
  facility_detail_side_mobile: { pc: '320×100 (모바일 가로)' },
  main_hero_mobile: { pc: '320×100 (모바일 가로)' },
  main_card_slider_mobile: { pc: '320×100 (모바일 가로)' },
}

/** 선택된 슬롯의 PC 이미지 힌트 문구. */
export function getPcImageHint(slotKey: string): string {
  const size = SLOT_SIZE_HINTS[slotKey]?.pc
  return size ? `권장 ${size}, 최대 5MB` : '최대 5MB'
}

/**
 * 슬롯 키별 노출 위치 설명.
 *
 * 어드민에서 슬롯 키만 보면 어디에 노출되는지 알기 어려우므로 셀렉트 하단에
 * 함께 표시한다. 위치/노출 형식 변경 시 본 상수 + 영구지침(§3)을 함께 업데이트한다.
 */
export const SLOT_LOCATION_LABELS: Record<string, string> = {
  main_hero:
    '메인 페이지 · 통계 섹션 위 · 풀폭 캠페인 배너 (1개)',
  main_card_slider:
    '메인 페이지 좌측 최상단 · 단일 가로 배너 (새로고침마다 가중 랜덤 1개)',
  main_my_below:
    '메인 페이지 우측 사이드바 · 로그인 카드 바로 아래 가로 배너 (1개)',
  main_side_ad_grid:
    '메인 페이지 우측 사이드바 · 2×2 정사각 그리드 (최대 4개)',
  main_mobile_inline:
    '메인 페이지 모바일 전용 · 통계 위 인라인 카드 (1개)',
  main_shop_card:
    '메인 페이지 하단 4-col 위젯 영역 · 쇼핑 추천 카드 (최대 4개)',
  search_list_4:
    '시설 검색 결과 페이지 · 4번째 카드 자리 (1개)',
  search_list_12:
    '시설 검색 결과 페이지 · 12번째 카드 자리 (1개)',
  facility_detail_side:
    '시설 상세 페이지 · PC 우측 사이드 / 모바일 본문 중간 (1개)',
  simulator_result_side:
    '비용 시뮬레이터 결과 페이지 · 결과 영역 사이드 (1개)',
  guide_bottom:
    '가이드/블로그 본문 하단 · CTA 직후 (1개)',

  // 좌우 세로배너 160×600 (PC 초광폭 2xl~ 에서만 노출)
  home_side_left:
    '메인 홈 · PC 좌측 여백 세로 배너 (초광폭 2xl~, 1개)',
  home_side_right:
    '메인 홈 · PC 우측 여백 세로 배너 (초광폭 2xl~, 1개)',
  facility_detail_sticky_left:
    '시설 상세 · PC 좌측 여백 세로 배너 (초광폭 2xl~, 1개)',
  facility_detail_sticky_right:
    '시설 상세 · PC 우측 여백 세로 배너 (초광폭 2xl~, 1개)',
  facility_search_side_right:
    '시설 검색(지도) · PC 우측 지도 옆 세로 배너 (xl~, 1개)',
  checklist_side_left:
    '체크리스트(시작/문항/결과) · PC 좌측 여백 세로 배너 (xl~, 1개)',
  checklist_side_right:
    '체크리스트(시작/문항/결과) · PC 우측 여백 세로 배너 (xl~, 1개)',
  simulator_side_left:
    '요양비용 계산기 · PC 좌측 여백 세로 배너 (초광폭 2xl~, 1개)',
  simulator_side_right:
    '요양비용 계산기 · PC 우측 여백 세로 배너 (초광폭 2xl~, 1개)',
  community_post_side_left:
    '커뮤니티 게시글 상세 · PC 좌측 여백 세로 배너 (초광폭, 1개)',
  community_post_side_right:
    '커뮤니티 게시글 상세 · PC 우측 여백 세로 배너 (초광폭, 1개)',
  community_board_side_left:
    '커뮤니티 게시판 목록 · PC 좌측 여백 세로 배너 (초광폭, 1개)',
  community_board_side_right:
    '커뮤니티 게시판 목록 · PC 우측 여백 세로 배너 (초광폭, 1개)',

  // 모바일 전용 가로 배너 320×100
  facility_detail_side_mobile:
    '시설 상세 · 모바일 본문 중간 가로 배너 (1개)',
  main_hero_mobile:
    '메인 홈 · 모바일 상단 히어로 아래 가로 배너 (1개)',
  main_card_slider_mobile:
    '메인 홈 · 모바일 상단 2번째 가로 배너 (1개)',
}

/** 선택된 슬롯의 노출 위치 설명. 매핑 없으면 빈 문자열. */
export function getSlotLocationLabel(slotKey: string): string {
  return SLOT_LOCATION_LABELS[slotKey] ?? ''
}

/** 선택된 슬롯의 모바일 이미지 힌트 문구. */
export function getMobileImageHint(slotKey: string): string {
  const mobile = SLOT_SIZE_HINTS[slotKey]?.mobile
  if (mobile) return `권장 ${mobile}, 비우면 PC용 이미지 사용`
  return '비우면 PC용 이미지 사용 (이 슬롯은 단일 사이즈)'
}

/**
 * 백엔드 슬롯 목록에 애드핏이 매핑된 모든 슬롯(SLOT_ADFIT_UNITS)을 병합한다.
 *
 * 애드핏이 노출되는 위치는 전부 어드민에서 하우스(클라이언트) 배너로 대체 등록할 수
 * 있어야 한다(하우스 우선 → 애드핏 fallback, PromotionSlot 참조). 백엔드
 * AVAILABLE_SLOT_KEYS 는 하우스 전용 슬롯의 canonical 목록이고, 여기에 애드핏 슬롯을
 * 합쳐 어드민 드롭다운을 구성하면 애드핏 슬롯을 새로 추가해도 별도 동기화 없이
 * 자동으로 등록 대상에 포함된다(과거 동기화 누락으로 사이드/모바일 슬롯이 등록
 * 불가였던 문제 해소).
 */
function mergeWithAdfitSlots(base: readonly string[]): string[] {
  const seen = new Set(base)
  const merged = [...base]
  for (const key of Object.keys(SLOT_ADFIT_UNITS)) {
    if (!seen.has(key)) {
      seen.add(key)
      merged.push(key)
    }
  }
  return merged
}

export function useAdminSlotKeys() {
  return useQuery({
    queryKey: ['admin-promotion-slot-keys'],
    queryFn: async () => {
      try {
        const res = await apiClient.get('/admin/promotions/slot-keys')
        const data = (res.data?.data ?? []) as string[]
        return mergeWithAdfitSlots(data.length > 0 ? data : FALLBACK_SLOT_KEYS)
      } catch {
        // API 실패 시에도 어드민 등록이 막히지 않도록 fallback 사용
        return mergeWithAdfitSlots(FALLBACK_SLOT_KEYS)
      }
    },
    initialData: mergeWithAdfitSlots(FALLBACK_SLOT_KEYS),
    staleTime: Infinity,
  })
}

export function useCreatePromotion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: PromotionRequest) => {
      const res = await apiClient.post('/admin/promotions', data)
      return res.data.data as Promotion
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-promotions'] })
    },
  })
}

export function useUpdatePromotion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: PromotionRequest }) => {
      const res = await apiClient.patch(`/admin/promotions/${id}`, data)
      return res.data.data as Promotion
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-promotions'] })
    },
  })
}

export function useDeletePromotion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/admin/promotions/${id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-promotions'] })
    },
  })
}
